<script setup lang="ts">
  import ShapeFileUploadDialog from '@/components/shapeFileUpload/ShapeFileUploadDialog.vue'
  import DeleteDialog from '@/components/dialog/DeleteDialog.vue'
  import type { SectionLine, SectionLineUploadCollection } from '@/stores/type/sectionLine.type'
  import UpdatePolygonDialog from '@/components/projectDetail/ProfileDrawer/UpdatePolygonDialog.vue'
  import ProfileCollectionIndex from '@/components/projectDetail/ProfileDrawer/ProfileCollectionIndex.vue'
  import UpdateSectionLineDialog from '@/components/projectDetail/ProfileDrawer/UpdateSectionLineDialog.vue'
  import CreateOrUpdateDialog from '@/components/dialog/CreateOrUpdateDialog.vue'
  import { computed } from 'vue'
  import ConfirmSectionLineSelectionDialog from '@/components/projectDetail/ProfileDrawer/ConfirmSectionLineSelectionDialog.vue'
  import { DEFAULT_PROFILE_DRAWER_WIDTH } from '@/stores/project'
  import HeaderWithActions from '@/components/projectDetail/components/HeaderWithActions.vue'
  import PrimaryButton from '@/components/shared/PrimaryButton.vue'
  import ImportSectionLineDialog from './ImportSectionLineDialog.vue'
  import ExportSectionLineDialog from '@/components/projectDetail/ProfileDrawer/ExportSectionLineDialog.vue'

  const store = useProjectStore()
  const giPointStore = useGIPointsStore()
  const sectionLineUploadCollectionsStore = useSectionLineUploadCollectionsStore()
  const sectionLinesStore = useSectionLinesStore()
  const selectionPolygonsStore = useSelectionPolygonsStores()
  const authorizationStore = useAuthorizationStore()

  const { isLengthProfileGraphBarShown } = storeToRefs(store)
  const { selectedItems: selectedSectionLineCollections } = storeToRefs(
    sectionLineUploadCollectionsStore
  )
  const { selectedItems: selectedSectionLines } = storeToRefs(sectionLinesStore)
  const profileDrawer = ref<HTMLElement | null>(null)
  const { items: giPoints } = storeToRefs(giPointStore)
  const { items: sectionLines } = storeToRefs(sectionLinesStore)
  const isGraphViewDisabled = computed(() => {
    return giPoints.value.length === 0 && sectionLines.value.length === 0
  })
  const canAdd = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: 'add_sectionlineuploadcollection'
    })
  })

  useResizeableElement({
    ref: profileDrawer,
    widthProperty: 'profileDrawer',
    elementGetter: vDrawerElementGetter
  })

  const postOrUpdate = async (item: SectionLineUploadCollection) => {
    const response = await sectionLineUploadCollectionsStore.postOrUpdate(item)
    if (response) {
      sectionLineUploadCollectionsStore.toggleCreateUpdateDialog()
    }
  }

  const onSectionLineCollectionDelete = (deletedItems: SectionLineUploadCollection[]) => {
    deletedItems.forEach((deletedItem) => {
      deletedItem.sectionline_set.features.forEach((sectionLine: SectionLine) => {
        sectionLinesStore.setCurrentItem({})
        sectionLinesStore.removeItem(sectionLine)
        selectionPolygonsStore.removePolygonsBySectionLineID(sectionLine.id)
      })
    })

    sectionLineUploadCollectionsStore.clearSelectedSectionLineCollections()
    sectionLinesStore.clearSelectedSectionLine()
  }

  const onSectionLineDelete = (deletedItems: SectionLine[]) => {
    deletedItems.forEach((deletedItem) => {
      sectionLineUploadCollectionsStore.removeSectionLineFromCollection(deletedItem)
      selectionPolygonsStore.removePolygonsBySectionLineID(deletedItem.id)
    })
    sectionLinesStore.clearSelectedSectionLine()
  }

  watch(
    () => isLengthProfileGraphBarShown.value,
    (newValue) => {
      if (newValue) {
        store.profileDrawer.width = DEFAULT_PROFILE_DRAWER_WIDTH
      } else {
        store.profileDrawer.width = DEFAULT_DATA_MANAGEMENT_DRAWER_WIDTH
      }
    }
  )
</script>

<template>
  <v-navigation-drawer
    :temporary="true"
    :scrim="false"
    ref="profileDrawer"
    :model-value="store.profileDrawer.shown"
    :width="store.profileDrawer.width"
    :class="['profile-drawer', 'resize-handler']"
  >
    <HeaderWithActions>
      <template #default> Section Line Configuration </template>
      <template #actions>
        <PrimaryButton v-if="canAdd && !isLengthProfileGraphBarShown" prepend-icon="mdi-plus">
          Add
          <v-menu activator="parent">
            <v-list>
              <v-list-item
                @click="
                  sectionLineUploadCollectionsStore.toggleAddSectionLineShapeFileDialog('Upload')
                "
                title="Upload Shape Files"
                prepend-icon="mdi-upload"
              ></v-list-item>
              <v-list-item
                title="Create a new Dataset of Section Lines"
                prepend-icon="mdi-plus"
                @click="
                  sectionLineUploadCollectionsStore.toggleAddSectionLineShapeFileDialog('Create')
                "
              >
              </v-list-item>
              <v-list-item
                title="Receive Section Lines from Speckle"
                prepend-icon="mdi-import"
                @click="
                  sectionLineUploadCollectionsStore.toggleImportSectionLineFromSpeckleDialog()
                "
              >
              </v-list-item>
            </v-list>
          </v-menu>
        </PrimaryButton>
        <PrimaryButton
          prepend-icon="mdi-chart-timeline-variant"
          :disabled="isGraphViewDisabled"
          @click="store.toggleGraphType()"
        >
          Graph Viewer
        </PrimaryButton>
        <PrimaryButton
          v-if="canAdd && !isLengthProfileGraphBarShown"
          prepend-icon="mdi-share"
          :disabled="isGraphViewDisabled"
          @click="sectionLineUploadCollectionsStore.toggleExportSectionLineToSpeckleDialog"
        >
          Send Speckle
        </PrimaryButton>
      </template>
      <template #subheader>
        Upload and manage section lines, polygons for visualizing ground investigation profiles
      </template>
    </HeaderWithActions>
    <v-divider />
    <ProfileCollectionIndex />
  </v-navigation-drawer>
  <ShapeFileUploadDialog />
  <CreateOrUpdateDialog
    :store="sectionLineUploadCollectionsStore"
    :name="sectionLineUploadCollectionsStore.currentItem.name"
    @save="postOrUpdate"
  />
  <DeleteDialog
    :bulk-delete="true"
    :items-to-delete="selectedSectionLineCollections"
    :store="sectionLineUploadCollectionsStore"
    @deleted="(deletedItems: SectionLineUploadCollection[]) =>onSectionLineCollectionDelete(deletedItems)"
  />
  <DeleteDialog
    :bulk-delete="true"
    :items-to-delete="selectedSectionLines"
    :store="sectionLinesStore"
    @deleted="(deletedItem:SectionLine[]) => onSectionLineDelete(deletedItem)"
  ></DeleteDialog>
  <UpdatePolygonDialog />
  <UpdateSectionLineDialog />
  <ConfirmSectionLineSelectionDialog />
  <ImportSectionLineDialog />
  <ExportSectionLineDialog />
</template>

<style scoped>
  .resize-handler {
    border-right-width: 10px;
    z-index: 1;
    box-shadow: none;
  }

  .hidden {
    display: none;
    opacity: 0;
    transition: opacity 0.5s ease;
  }

  :deep(.v-navigation-drawer__content) {
    overflow-y: hidden;
  }

  .profile-drawer {
    transition: border-bottom-color 0.3s;
    border-right: 10px solid rgba(0, 0, 0, 0.1);
  }
</style>
